import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { Card, ErrorNotice, Loading } from "./Ui";
import { label } from "../utils";
import { usePermissions } from "../permissions";

type Plan = {
  id: string;
  department: string;
  label: string;
  start: string;
  end: string;
  availableHours: number;
};
type Allocation = {
  initiativeId: number;
  planId: string;
  remainingHours: number;
  pausedPolicy: "retain" | "release";
};
type Config = { version: number; plans: Plan[]; allocations: Allocation[] };
type Item = {
  id: number;
  name: string;
  team: string;
  deliveryState: string | null;
};
type Overview = {
  config: Config;
  manage: boolean;
  integrationsPaused: boolean;
  demand: Item[];
  planningRequired: Item[];
  initiatives: Item[];
  periods: (Plan & {
    hours: number;
    ratio: number | null;
    status: string;
    missing: number;
    allocations: (Allocation & {
      name: string;
      deliveryState: string | null;
      countedHours: number;
    })[];
  })[];
};
const initiativeLink = (i: Item) => (
  <Link to={`/initiatives/SPD-PA-${String(i.id).padStart(3, "0")}`}>
    {i.name}
  </Link>
);

export function NativeCapacitySummary() {
  const api = useApi();
  const permissions = usePermissions();
  const data = useQuery({
    queryKey: ["native-capacity"],
    queryFn: () => api.get<Overview>("/native-capacity/"),
    enabled: permissions.nativeCapacity,
    refetchInterval: 60000,
  });
  if (!permissions.nativeCapacity) return null;
  return <Card className="span-2">
    <div className="card-header"><h2>Initiative capacity health</h2><Link to="/capacity">View capacity details</Link></div>
    <p className="section-help">Dashboard-native planning only. Submitted requests do not consume capacity; approval adds demand, then delivery allocations commit remaining work hours.</p>
    {data.isPending ? <Loading /> : data.isError ? <ErrorNotice error={data.error} /> : <>
      <p>{data.data.demand.length} approved requests awaiting scheduling · {data.data.planningRequired.length} initiatives need allocations.</p>
      {!data.data.periods.length ? <p>No delivery periods configured yet. Set department availability in Capacity before assessing workload health.</p> : <div className="table-wrap"><table>
        <thead><tr><th>Department / period</th><th>Remaining work</th><th>Available</th><th>Health</th></tr></thead>
        <tbody>{data.data.periods.map(p => <tr key={p.id}><td>{p.department} · {p.label}<small className="table-secondary">{p.start} – {p.end}</small></td><td>{p.hours} hours</td><td>{p.availableHours} hours</td><td>{p.status}</td></tr>)}</tbody>
      </table></div>}
      <p className="section-help">Health reflects entered availability and allocations, not measured productivity. Native hours and imported complexity points are separate and must not be added together.</p>
    </>}
  </Card>;
}

export function NativeCapacity() {
  const api = useApi();
  const client = useQueryClient();
  const data = useQuery({
    queryKey: ["native-capacity"],
    queryFn: () => api.get<Overview>("/native-capacity/"),
    refetchInterval: 60000,
  });
  const [editing, setEditing] = useState(false);
  if (data.isPending)
    return (
      <Card className="span-2">
        <Loading label="Loading initiative capacity…" />
      </Card>
    );
  if (data.isError)
    return (
      <Card className="span-2">
        <ErrorNotice error={data.error} />
      </Card>
    );
  const value = data.data;
  return (
    <>
      <Card className="span-2">
        <h2>Initiative capacity planning</h2>
        <p className="section-help">
          Source: dashboard initiatives. Submitted and triage requests do not
          consume capacity. Approval adds demand; Planned and In delivery work
          counts only after an administrator allocates remaining work hours to a
          delivery period. Sprint duration is not converted into hours.
        </p>
        {value.integrationsPaused && (
          <p className="notice">
            Linear and other external imports remain paused. This view does not
            use historical imported workload.
          </p>
        )}
        {value.manage && (
          <button className="btn-primary" onClick={() => setEditing(!editing)}>
            {editing ? "Close planning editor" : "Manage capacity plan"}
          </button>
        )}
        {editing && value.manage && (
          <PlanEditor
            initial={value.config}
            items={value.initiatives}
            onSaved={async () => {
              setEditing(false);
              await client.invalidateQueries({ queryKey: ["native-capacity"] });
            }}
          />
        )}
        {!value.periods.length ? (
          <p>
            No delivery periods configured yet. Approved initiatives remain
            visible below until planning is completed.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Department / period</th>
                  <th>Remaining allocation</th>
                  <th>Available hours</th>
                  <th>Health</th>
                </tr>
              </thead>
              <tbody>
                {value.periods.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.department} · {p.label}
                      <br />
                      <small>
                        {p.start} – {p.end}
                      </small>
                    </td>
                    <td>{p.hours} hours</td>
                    <td>{p.availableHours} hours</td>
                    <td>
                      {p.status}
                      {p.missing > 0 && (
                        <>
                          <br />
                          <small>
                            {p.missing} initiative(s) need current/future
                            allocation
                          </small>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="section-help">
          Available hours must cover the same remaining planning window as the
          allocations and exclude leave and other commitments. Health reflects
          entered data only; update both as work progresses. Past periods are
          retained, not treated as current capacity. No individual performance
          scoring.
        </p>
      </Card>
      <Card className="span-2">
        <h2>Approved demand awaiting scheduling ({value.demand.length})</h2>
        <p className="section-help">
          Visible immediately after approval. Not counted as committed workload
          until a delivery state and allocation are set.
        </p>
        {value.demand.length ? (
          <ul>
            {value.demand.map((i) => (
              <li key={i.id}>
                {initiativeLink(i)} · {i.team}
              </li>
            ))}
          </ul>
        ) : (
          <p>No unscheduled approved demand.</p>
        )}
      </Card>
      <Card className="span-2">
        <h2>Planning required ({value.planningRequired.length})</h2>
        <p className="section-help">
          Planned, In delivery or Paused initiatives without a current/future
          allocation. These prevent a “within capacity” result for their
          department.
        </p>
        {value.planningRequired.length ? (
          <ul>
            {value.planningRequired.map((i) => (
              <li key={i.id}>
                {initiativeLink(i)} · {i.team} · {label(i.deliveryState ?? "")}
              </li>
            ))}
          </ul>
        ) : (
          <p>No missing allocations among scheduled approved initiatives.</p>
        )}
      </Card>
      {value.periods.map((p) => (
        <Card key={p.id} className="span-2">
          <h2>
            {p.department} · {p.label}: allocations
          </h2>
          {p.allocations.length ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Initiative</th>
                    <th>Delivery state</th>
                    <th>Remaining hours entered</th>
                    <th>Hours counted</th>
                    <th>When paused</th>
                  </tr>
                </thead>
                <tbody>
                  {p.allocations.map((a) => (
                    <tr key={a.initiativeId}>
                      <td>
                        {initiativeLink({
                          id: a.initiativeId,
                          name: a.name,
                          team: p.department,
                          deliveryState: a.deliveryState,
                        })}
                      </td>
                      <td>{label(a.deliveryState ?? "Unscheduled")}</td>
                      <td>{a.remainingHours}</td>
                      <td>{a.countedHours}</td>
                      <td>{label(a.pausedPolicy)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No allocations.</p>
          )}
        </Card>
      ))}
    </>
  );
}

function PlanEditor({
  initial,
  items,
  onSaved,
}: {
  initial: Config;
  items: Item[];
  onSaved: () => Promise<void>;
}) {
  const api = useApi();
  const [draft, setDraft] = useState<Config>(() => structuredClone(initial));
  const [reason, setReason] = useState("");
  const [period, setPeriod] = useState("");
  const [initiative, setInitiative] = useState("");
  const [hours, setHours] = useState("");
  const [policy, setPolicy] = useState<"retain" | "release">("retain");
  const [error, setError] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api.put("/native-capacity/", { config: draft, changeReason: reason }),
    onSuccess: onSaved,
  });
  const field = (id: string, patch: Partial<Plan>) =>
    setDraft((v) => ({
      ...v,
      plans: v.plans.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }));
  const selected = draft.plans.find((p) => p.id === period);
  return (
    <form
      className="native-capacity-editor"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h3>Department delivery periods</h3>
      <p>
        Use non-overlapping periods for each department. Split work across
        periods without duplicating its remaining hours. A zero-hour allocation
        explicitly means no remaining work.
      </p>
      <fieldset disabled={save.isPending}>
        {draft.plans.map((p) => (
          <div className="form-grid" key={p.id}>
            <label>
              Department
              <input
                required
                minLength={2}
                maxLength={120}
                value={p.department}
                list="native-departments"
                onChange={(e) => field(p.id, { department: e.target.value })}
              />
            </label>
            <label>
              Period name
              <input
                required
                minLength={2}
                maxLength={120}
                value={p.label}
                onChange={(e) => field(p.id, { label: e.target.value })}
              />
            </label>
            <label>
              Start
              <input
                required
                type="date"
                value={p.start}
                onChange={(e) => field(p.id, { start: e.target.value })}
              />
            </label>
            <label>
              End
              <input
                required
                type="date"
                min={p.start}
                value={p.end}
                onChange={(e) => field(p.id, { end: e.target.value })}
              />
            </label>
            <label>
              Available remaining team hours
              <input
                required
                type="number"
                min="0"
                max="1000000"
                step="0.25"
                value={p.availableHours}
                onChange={(e) =>
                  field(p.id, { availableHours: Number(e.target.value) })
                }
              />
            </label>
            {!draft.allocations.some((a) => a.planId === p.id) && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() =>
                  setDraft((v) => ({
                    ...v,
                    plans: v.plans.filter((row) => row.id !== p.id),
                  }))
                }
              >
                Remove empty period
              </button>
            )}
          </div>
        ))}
        <datalist id="native-departments">
          {[...new Set(items.map((i) => i.team))].map((d) => (
            <option key={d} value={d} />
          ))}
        </datalist>
        <button
          type="button"
          className="btn-secondary"
          onClick={() =>
            setDraft((v) => ({
              ...v,
              plans: [
                ...v.plans,
                {
                  id: crypto.randomUUID(),
                  department: "",
                  label: "",
                  start: "",
                  end: "",
                  availableHours: 0,
                },
              ],
            }))
          }
        >
          Add delivery period
        </button>
        <h3>Allocate or update remaining work</h3>
        <div className="form-grid">
          <label>
            Delivery period
            <select
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value);
                setInitiative("");
              }}
            >
              <option value="">Choose period</option>
              {draft.plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.department} · {p.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Initiative
            <select
              value={initiative}
              onChange={(e) => setInitiative(e.target.value)}
            >
              <option value="">Choose initiative</option>
              {items
                .filter(
                  (i) =>
                    i.team === selected?.department &&
                    ["planned", "in_delivery", "paused"].includes(
                      i.deliveryState ?? "",
                    ),
                )
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Remaining work hours
            <input
              type="number"
              min="0"
              max="1000000"
              step="0.25"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </label>
          <label>
            If initiative is paused
            <select
              value={policy}
              onChange={(e) =>
                setPolicy(e.target.value as "retain" | "release")
              }
            >
              <option value="retain">Retain reserved capacity</option>
              <option value="release">Release reserved capacity</option>
            </select>
          </label>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            if (
              !period ||
              !initiative ||
              hours === "" ||
              !Number.isFinite(Number(hours)) ||
              Number(hours) < 0
            ) {
              setError(
                "Choose a period, initiative and non-negative remaining hours.",
              );
              return;
            }
            setError("");
            setDraft((v) => ({
              ...v,
              allocations: [
                ...v.allocations.filter(
                  (a) =>
                    a.planId !== period ||
                    a.initiativeId !== Number(initiative),
                ),
                {
                  planId: period,
                  initiativeId: Number(initiative),
                  remainingHours: Number(hours),
                  pausedPolicy: policy,
                },
              ],
            }));
          }}
        >
          Apply allocation to draft
        </button>
        <ul>
          {draft.allocations.map((a) => (
            <li key={`${a.planId}:${a.initiativeId}`}>
              {items.find((i) => i.id === a.initiativeId)?.name ??
                `Initiative ${a.initiativeId}`}{" "}
              · {draft.plans.find((p) => p.id === a.planId)?.label} ·{" "}
              {a.remainingHours} hours · paused: {a.pausedPolicy}{" "}
              <button
                type="button"
                className="btn-secondary"
                aria-label={`Remove allocation for ${items.find((i) => i.id === a.initiativeId)?.name ?? `Initiative ${a.initiativeId}`} from ${draft.plans.find((p) => p.id === a.planId)?.label ?? "period"}`}
                onClick={() =>
                  setDraft((v) => ({
                    ...v,
                    allocations: v.allocations.filter(
                      (row) =>
                        row.planId !== a.planId ||
                        row.initiativeId !== a.initiativeId,
                    ),
                  }))
                }
              >
                Remove allocation
              </button>
            </li>
          ))}
        </ul>
        <p className="section-help">
          Allocation removals apply only after saving this plan with a change
          reason. Removing the last allocation lets you remove its empty period.
        </p>
        <label>
          Reason for changes
          <input
            required
            minLength={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        {error && <p role="alert">{error}</p>}
        {save.isError && <ErrorNotice error={save.error} />}
        <button className="btn-primary" type="submit">
          {save.isPending ? "Saving…" : "Save audited capacity plan"}
        </button>
      </fieldset>
    </form>
  );
}
